import type { LucideIcon } from 'lucide-react';
import {
  Home,
  Settings,
  Users,
  Shield,
  Package,
  Warehouse,
  ArrowDownToLine,
  ArrowUpFromLine,
  LayoutDashboard,
  ClipboardList,
  FileText,
  BookOpen,
  Sparkles,
  Megaphone,
  ShoppingCart,
  Download,
  FileSpreadsheet,
  FileCheck,
  Briefcase,
  BellRing,
  Activity,
  HeartPulse,
  Layers,
  FileStack,
} from 'lucide-react';
import { PERMISSIONS } from '@features/shared/constants/permissions';

/**
 * THE nav tree. Declared here and nowhere else — the legacy `Sidebar` and the M1 icon rail both
 * read this one array, so an entry added here reaches every shell surface at once.
 *
 * It moved out of `Sidebar.tsx` in M1 (86d44z1g9) precisely so the rail could share it while the
 * old sidebar still renders alongside. M2 deletes the sidebar; it does not delete this file.
 */

/** Any one of these grants the Document Capture page; it narrows itself to what you may see. */
export const DOCSCAN_VIEW_PERMISSIONS = [
  PERMISSIONS.DOCSCAN_VIEW_DR,
  PERMISSIONS.DOCSCAN_VIEW_INV,
  PERMISSIONS.DOCSCAN_VIEW_INT,
  PERMISSIONS.DOCSCAN_VIEW_CM,
  PERMISSIONS.DOCSCAN_VIEW_OP,
  PERMISSIONS.DOCSCAN_VIEW_DV,
  PERMISSIONS.DOCSCAN_VIEW_JV,
  // Upload-only station account (86d3rrfee, must-fix #2) — must see the nav item to reach the page
  // that hosts the folder watcher; matches the route's anyOf.
  PERMISSIONS.DOCSCAN_UPLOAD,
];

export interface MenuLeaf {
  icon: LucideIcon;
  label: string;
  href: string;
  permissions?: string[];
  badge?: number;
  children?: never;
}

export interface MenuSubGroup {
  icon: LucideIcon;
  label: string;
  href?: never;
  permissions?: string[];
  children: MenuLeaf[];
}

export type MenuChild = MenuLeaf | MenuSubGroup;

export interface MenuItem {
  type: 'item';
  /** Stable rail identity. Never derived from the label — the label is copy, this is a key. */
  id: string;
  icon: LucideIcon;
  label: string;
  /** Rail label, which has ~54px to work with. Falls back to `label`. */
  short?: string;
  href: string;
  permissions?: string[];
  badge?: number;
  /** Draw the rail's hairline separator above this entry. Ignored by the legacy sidebar. */
  dividerBefore?: boolean;
  /**
   * What this module's badge counts, for the rail's accessible name ("Home — 2 new
   * announcements"). The badge itself is `aria-hidden`, so without this the number is
   * unreachable by screen reader.
   */
  railBadgeNoun?: string;
}

export interface MenuGroup {
  type: 'group';
  id: string;
  icon: LucideIcon;
  label: string;
  short?: string;
  children: MenuChild[];
  /** Small uppercase tag beside the label (e.g. SHARED for cross-department sections). */
  tag?: string;
  dividerBefore?: boolean;
  /** See `MenuItem.railBadgeNoun`. */
  railBadgeNoun?: string;
}

export type MenuEntry = MenuItem | MenuGroup;

export const menuItems: MenuEntry[] = [
  { type: 'item', id: 'home', icon: Home, label: 'Home', href: '/', railBadgeNoun: 'new announcements' },
  {
    type: 'group',
    id: 'administration',
    icon: Settings,
    label: 'Administration',
    // The only entry whose label does not fit the rail's 54px box.
    short: 'Admin',
    children: [
      { icon: Users, label: 'User Management', href: '/administration/users', permissions: [PERMISSIONS.USER_VIEW_USER_LIST] },
      { icon: Shield, label: 'Authorization', href: '/administration/authorization', permissions: [PERMISSIONS.AUTH_VIEW_ROLE_LIST, PERMISSIONS.AUTH_VIEW_USER_ROLE_LIST] },
      { icon: Package, label: 'Modules', href: '/administration/modules', permissions: [PERMISSIONS.MOD_VIEW_MODULE_LIST] },
      { icon: Megaphone, label: 'Release Notes', href: '/administration/release-notes', permissions: [PERMISSIONS.RELEASE_MANAGE_RELEASES] },
      { icon: BellRing, label: 'Announcements', href: '/administration/announcements', permissions: [PERMISSIONS.ANNOUNCEMENT_VIEW] },
      { icon: Activity, label: 'Activity', href: '/activity', permissions: [PERMISSIONS.ACTIVITY_VIEW_ALL] },
      { icon: HeartPulse, label: 'System Health', href: '/system-health', permissions: [PERMISSIONS.SYSTEM_VIEW_HEALTH] },
    ],
  },
  {
    type: 'group',
    id: 'mro',
    icon: Warehouse,
    label: 'MRO',
    children: [
      { icon: LayoutDashboard, label: 'Dashboard', href: '/mro/dashboard', permissions: [PERMISSIONS.ISSUANCE_VIEW_DASHBOARD] },
      {
        icon: ArrowDownToLine,
        label: 'Receiving',
        permissions: [PERMISSIONS.RECEIVING_VIEW, PERMISSIONS.RECEIVING_CREATE],
        children: [
          { icon: ShoppingCart, label: 'Purchase Orders', href: '/mro/purchase-orders', permissions: [PERMISSIONS.RECEIVING_VIEW, PERMISSIONS.RECEIVING_CREATE] },
          { icon: Package, label: 'Put-Away', href: '/mro/receiving', permissions: [PERMISSIONS.RECEIVING_VIEW, PERMISSIONS.RECEIVING_CREATE] },
        ],
      },
      {
        icon: ArrowUpFromLine,
        label: 'Issuance',
        permissions: [PERMISSIONS.ISSUANCE_VIEW_PICKLIST, PERMISSIONS.ISSUANCE_VIEW_REQUEST_LIST],
        children: [
          { icon: ClipboardList, label: 'Picklists', href: '/mro/issuance/picklists', permissions: [PERMISSIONS.ISSUANCE_VIEW_PICKLIST] },
          { icon: FileText, label: 'MRS', href: '/mro/issuance/mrs', permissions: [PERMISSIONS.ISSUANCE_VIEW_REQUEST_LIST] },
          { icon: BookOpen, label: 'Picklist Guide', href: '/mro/issuance/picklist-guide', permissions: [PERMISSIONS.ISSUANCE_VIEW_PICKLIST] },
        ],
      },
    ],
  },
  {
    type: 'group',
    id: 'sales',
    icon: Briefcase,
    label: 'Sales',
    // Child carries the permissions; the filter in <Sidebar/> already hides groups whose
    // every child was filtered out, so the whole "Sales" section disappears for users who
    // hold neither quotation-view permission.
    children: [
      {
        icon: FileSpreadsheet,
        label: 'Quotations',
        href: '/quotations',
        permissions: [
          PERMISSIONS.QUOTATIONS_VIEW_QUOTATION,
          PERMISSIONS.QUOTATIONS_VIEW_ALL_QUOTATIONS,
        ],
      },
      {
        icon: FileCheck,
        label: 'Sales Orders',
        href: '/sales-orders',
        permissions: [PERMISSIONS.SALES_ORDERS_VIEW_SALES_ORDER],
      },
    ],
  },
  {
    // 86d3rrfbf — Services: cross-department shared utilities, not owned by one department.
    // Document Capture is ONE item opening ONE page; the archive, Scan Station and review are
    // panels on it, deliberately not three menu entries.
    type: 'group',
    id: 'services',
    icon: Layers,
    label: 'Services',
    tag: 'SHARED',
    railBadgeNoun: 'documents to review',
    children: [
      {
        icon: FileStack,
        label: 'Document Capture',
        href: '/documents',
        // Any single family grants access — the page narrows itself to what you may see.
        permissions: DOCSCAN_VIEW_PERMISSIONS,
      },
    ],
  },
  { type: 'item', id: 'whats-new', icon: Sparkles, label: "What's New", href: '/release-notes', dividerBefore: true },
  { type: 'item', id: 'downloads', icon: Download, label: 'Downloads', href: '/downloads' },
];

/**
 * Narrows a child to the depth-2 section that carries its own leaves (only MRO has any).
 * A real type predicate, not an inline `'children' in child` check — without it TypeScript keeps
 * `href` as `string | undefined` on the other branch, because `MenuSubGroup` declares `href?: never`.
 */
export function isSubGroup(child: MenuChild): child is MenuSubGroup {
  return 'children' in child && Array.isArray(child.children);
}

/**
 * Every leaf href a module owns, flattened across all three depths. The rail compares the lit
 * href against this to decide whether a module "holds" the current route.
 */
export function moduleHrefs(entry: MenuEntry): string[] {
  if (entry.type === 'item') return [entry.href];
  return entry.children.flatMap((child) =>
    isSubGroup(child) ? child.children.map((sub) => sub.href) : [child.href]
  );
}

/** Every nav href in the tree, in declaration order. */
export const ALL_NAV_HREFS: string[] = menuItems.flatMap(moduleHrefs);

/**
 * The nav tree as a holder of `hasPermission` may see it (M9 · 86d44zh74).
 *
 * A PURE function, deliberately extracted from `useVisibleMenu` so it can be tested without React —
 * this repo has no jsdom or testing-library setup, and the review found a real, silent permission
 * regression waiting here (see the depth-3 note below). Badge injection stays in the hook, because
 * badges come from stores and would make this impure.
 *
 * Rules, all three load-bearing:
 *  • OR within an entry — holding ANY listed permission grants it. That mirrors how the routes are
 *    gated (`PermissionRoute anyOf`), so nav visibility and route access agree.
 *  • No `permissions` (or an empty array) means PUBLIC to any signed-in user — Home, What's New and
 *    Downloads rely on this.
 *  • A group whose every child filtered out does not exist for this user, so it does not render.
 *
 * ⚠️ THE DEPTH-3 BRANCH IS NOT DEAD CODE. M2's review found it about to be deleted on the theory
 * that "the tree flattens". It does not: MRO renders as section headers with indented leaves.
 * Removing that branch renders every MRO child to users who may not see them — invisible to an
 * admin, who holds all of the permissions. `menu.filter.test.ts` exists to make that impossible to
 * repeat.
 */
export function filterMenuByPermission(
  entries: readonly MenuEntry[],
  hasPermission: (permission: string) => boolean
): MenuEntry[] {
  const hasAny = (perms?: string[]) =>
    !perms || perms.length === 0 || perms.some((p) => hasPermission(p));

  return entries
    .map((entry): MenuEntry | null => {
      if (entry.type === 'item') return hasAny(entry.permissions) ? entry : null;

      const visibleChildren = entry.children
        .filter((child): child is MenuChild =>
          isSubGroup(child)
            ? // A section survives only if it is itself permitted AND at least one leaf under it is.
              hasAny(child.permissions) && child.children.some((sub) => hasAny(sub.permissions))
            : hasAny(child.permissions)
        )
        .map((child): MenuChild =>
          isSubGroup(child)
            ? { ...child, children: child.children.filter((sub) => hasAny(sub.permissions)) }
            : child
        );

      if (visibleChildren.length === 0) return null;
      return { ...entry, children: visibleChildren };
    })
    .filter((entry): entry is MenuEntry => entry !== null);
}

/**
 * The number the rail shows on a module button: the sum of its leaves' badges, at whatever depth
 * they sit. A module with nothing to report returns 0, which the rail renders as no badge at all.
 */
export function moduleBadge(entry: MenuEntry): number {
  if (entry.type === 'item') return entry.badge ?? 0;
  return entry.children.reduce((total, child) => {
    if (isSubGroup(child)) {
      return total + child.children.reduce((sum, sub) => sum + (sub.badge ?? 0), 0);
    }
    return total + (child.badge ?? 0);
  }, 0);
}
